import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  RateReview as FeedbackIcon,
} from '@mui/icons-material';
import { getFeedbacks, getFeedbackById, deleteFeedback } from '../../api/feedbackApi';
import { useToast } from '../../contexts/ToastContext';
import {
  PageHeader,
  ModernButton,
  ModernDataGrid,
} from '../../components/common';
import { ConfirmDialog } from '../../components/dialogs';

const FeedbacksManagement = () => {
  const { showToast } = useToast();
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(false);

  // Dialogs
  const [viewDialog, setViewDialog] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState(null);

  // Fetch feedbacks
  const fetchFeedbacks = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getFeedbacks();
      setFeedbacks(data);
    } catch (error) {
      console.error('Failed to fetch feedbacks:', error);
      showToast('Failed to fetch feedbacks', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    fetchFeedbacks();
  }, [fetchFeedbacks]);

  // Handlers
  const handleViewFeedback = async (feedback) => {
    try {
      const { data } = await getFeedbackById(feedback._id);
      setViewDialog(data);
    } catch (error) {
      console.error('Failed to fetch feedback details:', error);
      showToast('Failed to load feedback details', 'error');
    }
  };

  const handleDeleteFeedback = async () => {
    try {
      await deleteFeedback(deleteDialog._id);
      showToast('Feedback deleted successfully', 'success');
      setDeleteDialog(null);
      fetchFeedbacks();
    } catch (error) {
      console.error('Failed to delete feedback:', error);
      showToast(error.response?.data?.error || 'Failed to delete feedback', 'error');
    }
  };

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Truncate review text
  const truncateText = (text, maxLength = 100) => {
    if (!text) return 'No review';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  // Table columns
  const columns = [
    {
      field: '_id',
      headerName: 'ID',
      flex: 0.8,
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
            {params.value.substring(0, 8)}...
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: 'review',
      headerName: 'Review',
      flex: 2,
      renderCell: (params) => (
        <Tooltip title={params.value || 'No review'}>
          <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
            {truncateText(params.value)}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: 'createdAt',
      headerName: 'Created At',
      flex: 1,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
          {formatDate(params.value)}
        </Typography>
      ),
    },
    {
      field: 'updatedAt',
      headerName: 'Updated At',
      flex: 1,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
          {formatDate(params.value)}
        </Typography>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 0.8,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="View Details">
            <IconButton
              size="small"
              onClick={() => handleViewFeedback(params.row)}
              sx={{ color: 'primary.main' }}
            >
              <ViewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              onClick={() => setDeleteDialog(params.row)}
              sx={{ color: 'error.main' }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <PageHeader
        title="Feedbacks Management"
        subtitle="View and manage user feedback submissions"
        icon={<FeedbackIcon />}
      />

      <ModernDataGrid
        rows={feedbacks}
        columns={columns}
        loading={loading}
        getRowId={(row) => row._id}
        disablePagination
        autoHeight
      />

      {/* View Dialog */}
      <Dialog
        open={!!viewDialog}
        onClose={() => setViewDialog(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Feedback Details</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Feedback ID
              </Typography>
              <Typography variant="body1">{viewDialog?._id}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Review
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  whiteSpace: 'pre-wrap',
                  backgroundColor: 'background.default',
                  p: 2,
                  borderRadius: 1,
                  maxHeight: '300px',
                  overflowY: 'auto',
                }}
              >
                {viewDialog?.review || 'No review provided'}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 4 }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Created At
                </Typography>
                <Typography variant="body1">
                  {formatDate(viewDialog?.createdAt)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Updated At
                </Typography>
                <Typography variant="body1">
                  {formatDate(viewDialog?.updatedAt)}
                </Typography>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <ModernButton onClick={() => setViewDialog(null)} variant="outlined">
            Close
          </ModernButton>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={!!deleteDialog}
        onClose={() => setDeleteDialog(null)}
        onConfirm={handleDeleteFeedback}
        title="Delete Feedback"
        message={`Are you sure you want to delete this feedback? This action cannot be undone.`}
        confirmText="Delete"
        confirmColor="error"
      />
    </Box>
  );
};

export default FeedbacksManagement;
